import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Plus, Loader2, Search, CalendarIcon, Send, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatDateTime, dueColor, relTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import BulkActionBar from "@/components/BulkActionBar";
import TaskCalendar from "@/components/TaskCalendar";
import TaskDetailDialog from "@/components/TaskDetailDialog";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCorners,
} from "@dnd-kit/core";

import { STATUSES, PRIORITIES, CATEGORIES, PRIORITY_TONE, emptyTaskForm } from "@/constants/tasks";

export default function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [priorityF, setPriorityF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [mine, setMine] = useState(false);
  const [newOpen, setNewOpen] = useState(searchParams.get("new") === "1");
  const [selectedId, setSelectedId] = useState(searchParams.get("id"));
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [activeDragId, setActiveDragId] = useState(null);
  const [categoryF, setCategoryF] = useState(searchParams.get("category") || "all");
  const [counts, setCounts] = useState({});

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (priorityF !== "all") params.priority = priorityF;
    if (statusF !== "all") params.task_status = statusF;
    if (categoryF !== "all") params.category = categoryF;
    if (mine) params.mine = true;
    api.get("/tasks", { params }).then(({ data }) => {
      setTasks(data);
      setSelected(new Set());
    });
    api.get("/tasks/category-counts").then(({ data }) => setCounts(data)).catch(() => {});
  }, [q, priorityF, statusF, categoryF, mine]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/users").then(({ data }) => setUsers(data));
  }, []);

  const grouped = useMemo(() => {
    const g = { todo: [], in_progress: [], review: [], done: [] };
    tasks.forEach((t) => (g[t.task_status] || g.todo).push(t));
    return g;
  }, [tasks]);

  // Total pending across all categories — memoised so the JSX-rendered pill
  // does not recompute a `reduce` on every render.
  const totalPending = useMemo(
    () => Object.values(counts).reduce((a, b) => a + b, 0),
    [counts],
  );

  const [form, setForm] = useState(emptyTaskForm);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const openNew = () => { setNewOpen(true); setSearchParams({ new: "1" }); };
  const closeNew = () => {
    setNewOpen(false);
    searchParams.delete("new");
    setSearchParams(searchParams);
  };

  const createTask = async () => {
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || null,
        client_id: form.client_id === "none" ? null : form.client_id,
        assignee_id: form.assignee_id === "none" ? null : form.assignee_id,
        priority: form.priority,
        task_status: form.task_status,
        category: form.category === "none" ? null : form.category,
        due_date: form.due_date ? form.due_date.toISOString() : null,
      };
      await api.post("/tasks", payload);
      toast.success("Task created");
      closeNew();
      setForm(emptyTaskForm());
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to create task"));
    } finally {
      setBusy(false);
    }
  };

  const moveTask = async (taskId, newStatus) => {
    const original = tasks.find((t) => t.id === taskId);
    if (!original) return;
    // Optimistic update
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, task_status: newStatus } : t)));
    try {
      await api.patch(`/tasks/${taskId}`, { task_status: newStatus });
    } catch (e) {
      // Revert only this one card — avoids flicker of a full reload.
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, task_status: original.task_status } : t)));
      toast.error("Failed to move task");
    }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "";
  const userName = (id) => users.find((u) => u.id === id)?.full_name || "";

  // --------- Bulk ----------
  const toggleRow = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((s) => (s.size === tasks.length ? new Set() : new Set(tasks.map((t) => t.id))));
  };
  const bulkDelete = async () => {
    const reason = window.prompt(`Delete ${selected.size} task(s)? Reason (min 5 chars, required for audit):`);
    if (!reason || reason.trim().length < 5) return;
    try {
      const { data } = await api.post("/bulk/tasks/delete", { ids: Array.from(selected) }, { headers: { "X-Change-Reason": reason.trim() } });
      toast.success(`Deleted ${data.updated} task(s)`);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Bulk delete failed"));
    }
  };
  const bulkStatus = async (status) => {
    try {
      const { data } = await api.post("/bulk/tasks/update", { ids: Array.from(selected), task_status: status });
      toast.success(`Updated ${data.updated} task(s) → ${status.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error("Bulk update failed");
    }
  };
  const bulkAssign = async (userId) => {
    try {
      const { data } = await api.post("/bulk/tasks/update", { ids: Array.from(selected), assignee_id: userId });
      toast.success(`Reassigned ${data.updated} task(s)`);
      load();
    } catch (e) {
      toast.error("Bulk assign failed");
    }
  };

  // --------- DnD ---------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );
  const handleDragEnd = (e) => {
    setActiveDragId(null);
    const { active, over } = e;
    if (!over) return;
    const overStatus = STATUSES.find((s) => s.v === over.id)?.v;
    if (!overStatus) return;
    const task = tasks.find((t) => t.id === active.id);
    if (!task || task.task_status === overStatus) return;
    moveTask(active.id, overStatus);
  };
  const activeTask = activeDragId ? tasks.find((t) => t.id === activeDragId) : null;

  return (
    <div className="space-y-4" data-testid="tasks-page">
      {/* Category pill bar */}
      <div className="flex flex-wrap gap-2" data-testid="category-pills">
        <button
          onClick={() => setCategoryF("all")}
          className={cn(
            "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-2",
            categoryF === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted",
          )}
          data-testid="cat-pill-all"
        >
          All pending
          <span className={cn(
            "font-mono px-1.5 py-0.5 rounded text-[10px]",
            categoryF === "all" ? "bg-primary-foreground/20" : "bg-muted-foreground/10",
          )}>
            {totalPending}
          </span>
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c.v}
            onClick={() => setCategoryF(categoryF === c.v ? "all" : c.v)}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-2",
              categoryF === c.v ? c.tone.replace("bg-", "bg-").replace("/10", "/25") + " ring-1 ring-current/40" : c.tone,
            )}
            data-testid={`cat-pill-${c.v}`}
          >
            {c.label}
            <span className="font-mono text-[10px] bg-current/10 px-1.5 py-0.5 rounded">{counts[c.v] || 0}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-64">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search tasks..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="tasks-search" />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="h-9 w-40" data-testid="tasks-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityF} onValueChange={setPriorityF}>
          <SelectTrigger className="h-9 w-36" data-testid="tasks-filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={mine ? "default" : "outline"} size="sm" onClick={() => setMine((v) => !v)} data-testid="tasks-filter-mine">Only mine</Button>
        <div className="flex-1" />
        <Button onClick={openNew} size="sm" data-testid="new-task-btn"><Plus size={13} className="mr-1.5" /> New task</Button>
      </div>

      <Tabs defaultValue="kanban">
        <TabsList>
          <TabsTrigger value="kanban" data-testid="tab-kanban">Kanban</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">List</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="kanban">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={(e) => setActiveDragId(e.active.id)}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveDragId(null)}
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {STATUSES.map((col) => (
                <KanbanColumn
                  key={col.v}
                  id={col.v}
                  label={col.label}
                  tasks={grouped[col.v] || []}
                  clientName={clientName}
                  userName={userName}
                  onOpen={setSelectedId}
                />
              ))}
            </div>
            <DragOverlay>
              {activeTask ? (
                <TaskCard task={activeTask} clientName={clientName(activeTask.client_id)} assignee={userName(activeTask.assignee_id)} dragging />
              ) : null}
            </DragOverlay>
          </DndContext>
        </TabsContent>

        <TabsContent value="list">
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr className="text-left">
                  <th className="px-3 py-2 w-8">
                    <Checkbox
                      checked={tasks.length > 0 && selected.size === tasks.length}
                      onCheckedChange={toggleAll}
                      data-testid="tasks-select-all"
                      aria-label="Select all tasks"
                    />
                  </th>
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Task</th>
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Client</th>
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Assignee</th>
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Priority</th>
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Status</th>
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Due</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => {
                  const isSelected = selected.has(t.id);
                  return (
                  <tr
                    key={t.id}
                    onClick={(e) => {
                      if (e.target.closest("[data-row-checkbox]")) return;
                      setSelectedId(t.id);
                    }}
                    className={`border-b border-border hover:bg-muted/40 cursor-pointer ${isSelected ? "bg-primary/5" : ""}`}
                    data-testid={`task-row-${t.id}`}
                  >
                    <td className="px-3 py-2.5" data-row-checkbox>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleRow(t.id)}
                        data-testid={`task-select-${t.id}`}
                        aria-label={`Select ${t.title}`}
                      />
                    </td>
                    <td className="px-3 py-2.5 font-medium">{t.title}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{clientName(t.client_id) || "—"}</td>
                    <td className="px-3 py-2.5 text-xs">{userName(t.assignee_id) || "—"}</td>
                    <td className={cn("px-3 py-2.5 text-xs font-medium capitalize", PRIORITY_TONE[t.priority])}>{t.priority}</td>
                    <td className="px-3 py-2.5 text-xs capitalize">{t.task_status.replace("_", " ")}</td>
                    <td className={cn("px-3 py-2.5 text-xs font-mono", dueColor(t.due_date))}>{t.due_date ? formatDate(t.due_date, "dd MMM yy") : "—"}</td>
                  </tr>
                  );
                })}
                {tasks.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-muted-foreground">No tasks match. <button onClick={openNew} className="underline">Create one</button>.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="calendar">
          <TaskCalendar tasks={tasks} onSelectTask={(t) => setSelectedId(t.id)} />
        </TabsContent>
      </Tabs>

      <BulkActionBar
        count={selected.size}
        onClear={() => setSelected(new Set())}
        actions={[
          {
            label: "Mark done",
            onClick: () => bulkStatus("done"),
            variant: "outline",
            testid: "bulk-mark-done",
          },
          {
            label: "In progress",
            onClick: () => bulkStatus("in_progress"),
            variant: "outline",
            testid: "bulk-mark-progress",
          },
          {
            label: "Assign to me",
            onClick: async () => {
              const { data } = await api.get("/auth/me");
              bulkAssign(data.id);
            },
            variant: "outline",
            testid: "bulk-assign-me",
          },
          {
            label: "Delete",
            icon: <Trash2 size={13} />,
            onClick: bulkDelete,
            variant: "destructive",
            testid: "bulk-delete-tasks",
          },
        ]}
      />

      {/* New task dialog */}
      <Dialog open={newOpen} onOpenChange={(v) => (v ? openNew() : closeNew())}>
        <DialogContent className="max-w-lg" data-testid="new-task-dialog">
          <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-task-title" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="new-task-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="new-task-client"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Assignee</Label>
                <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                  <SelectTrigger data-testid="new-task-assignee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="new-task-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Status</Label>
                <Select value={form.task_status} onValueChange={(v) => setForm({ ...form, task_status: v })}>
                  <SelectTrigger data-testid="new-task-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="new-task-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Due date</Label>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start font-mono text-xs" data-testid="new-task-due">
                    <CalendarIcon size={12} className="mr-1.5" />
                    {form.due_date ? formatDate(form.due_date, "dd MMM yy") : "Set date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0" align="start">
                  <Calendar mode="single" selected={form.due_date} onSelect={(d) => { setForm({ ...form, due_date: d }); setDatePickerOpen(false); }} />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeNew}>Cancel</Button>
            <Button onClick={createTask} disabled={!form.title || busy} data-testid="new-task-submit">
              {busy && <Loader2 size={13} className="animate-spin mr-1.5" />}
              Create task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedId && (
        <TaskDetailDialog
          taskId={selectedId}
          clients={clients}
          users={users}
          onClose={() => setSelectedId(null)}
          onChange={load}
        />
      )}
    </div>
  );
}


function KanbanColumn({ id, label, tasks, clientName, userName, onOpen }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "min-w-0 rounded-md transition-colors",
        isOver ? "bg-primary/5 ring-1 ring-primary/40" : "",
      )}
      data-testid={`kanban-col-${id}`}
    >
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-xs font-heading uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="text-xs font-mono text-muted-foreground">{tasks.length}</div>
      </div>
      <div className="space-y-2 min-h-[80px] p-1">
        {tasks.map((t) => (
          <DraggableTaskCard
            key={t.id}
            task={t}
            clientName={clientName(t.client_id)}
            assignee={userName(t.assignee_id)}
            onOpen={() => onOpen(t.id)}
          />
        ))}
        {tasks.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-6 border border-dashed border-border rounded-md">Drop here</div>
        )}
      </div>
    </div>
  );
}


function DraggableTaskCard({ task, clientName, assignee, onOpen }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id });
  return (
    <div
      ref={setNodeRef}
      style={{ opacity: isDragging ? 0.35 : 1 }}
      className="relative"
      data-testid={`task-card-wrap-${task.id}`}
    >
      <TaskCard
        task={task}
        clientName={clientName}
        assignee={assignee}
        onOpen={onOpen}
        dragHandleProps={{ ...listeners, ...attributes }}
      />
    </div>
  );
}


function TaskCard({ task, clientName, assignee, onOpen, dragHandleProps, dragging }) {
  return (
    <Card
      className={cn(
        "p-3 border-border shadow-none space-y-1.5 select-none group bg-card",
        dragging && "shadow-lg ring-1 ring-primary/40 rotate-2",
      )}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start gap-2">
        {dragHandleProps && (
          <button
            {...dragHandleProps}
            className="opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing text-muted-foreground -ml-1 mt-0.5"
            aria-label="Drag task"
            data-testid={`drag-handle-${task.id}`}
            type="button"
          >
            <GripVertical size={12} />
          </button>
        )}
        <button
          className="text-sm font-medium flex-1 min-w-0 text-left"
          onClick={onOpen}
          type="button"
        >
          {task.title}
        </button>
        <div className={cn("text-[10px] uppercase font-medium tracking-widest", PRIORITY_TONE[task.priority])}>{task.priority}</div>
      </div>
      {clientName && <div className="text-xs text-muted-foreground truncate">{clientName}</div>}
      <div className="flex items-center gap-2 text-xs">
        {task.due_date && <span className={cn("font-mono", dueColor(task.due_date))}>{formatDate(task.due_date, "dd MMM")}</span>}
        <div className="flex-1" />
        {assignee && <span className="text-muted-foreground truncate">{assignee}</span>}
      </div>
    </Card>
  );
}
